import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import { API_BASE_URL, resolveCookieDomainAttribute } from '@/lib/api/base-url';
import { clearStoredReferralAttribution } from '@/lib/referrals/attribution';
import { POST_LOGOUT_PATH } from '@/lib/auth/login-route';
import { requestAuthScopedStateReset } from '@/lib/auth-scoped-state-reset';
import { translateClient } from '@/lib/i18n/client';
import { getClientBuildHeaders } from '@/lib/version/client-version';
import { dispatchImageTemplateCacheInvalidated } from '@/lib/template-cache-events';
import type { ReferralAuthPayload, ReferralBinding } from '@/types/referrals';
import { stageXRegistrationReceipt } from '@/lib/marketing/x-pixel';

const API_BASE = API_BASE_URL;
const WECHAT_QR_SESSION_TOKEN_HEADER = 'X-Wechat-QR-Session-Token';

// 调试开关
const DEBUG_ENABLED = process.env.NODE_ENV === 'development';
const debugError = (...args: unknown[]) => { if (DEBUG_ENABLED) console.error(...args); };
const _jsonHeaders = (extra?: Record<string, string>) => ({
  'Content-Type': 'application/json',
  ...getClientBuildHeaders(),
  ...(extra || {}),
});
const _clientHeaders = (extra?: Record<string, string>) => ({
  ...getClientBuildHeaders(),
  ...(extra || {}),
});

// ★ Token refresh 去重锁 — 防止多个并发调用同时刷新 token
let _refreshPromise: Promise<string | null> | null = null;

// ★ Session 校验单飞 — 防止 AuthGuard / authFetch / ApiClient 首屏并发重复刷新
let _sessionCheckPromise: Promise<void> | null = null;
let _forcedSessionCheckPromise: Promise<void> | null = null;
let _authSessionVersion = 0;
let _activeLoginAbortController: AbortController | null = null;
let _anonymousSessionProbeCompleted = false;

// ★ 上次 refresh 网络失败的时间戳 — 避免短时间内重复尝试（后端重启期间不做无用请求）
let _lastRefreshFailure = 0;
const _REFRESH_COOLDOWN_MS = 15_000; // 15 秒冷却
const _REFRESH_COOKIE_SYNC_RETRY_DELAY_MS = 250;
const _REFRESH_COOKIE_SYNC_MAX_ATTEMPTS = 2;
const _REFRESH_REQUEST_TIMEOUT_MS = 15_000;
const _SESSION_RECOVERY_TIMEOUT_MS = 8_000;
const _LOGIN_RECOVERY_TIMEOUT_MS = 5_000;
const _AUTH_MUTATION_LOCK_NAME = 'dobidobi-auth-mutation';
const _AUTH_SYNC_CHANNEL_NAME = 'dobidobi-auth-sync';
const _AUTH_SYNC_STORAGE_KEY = 'dobidobi-auth-sync-epoch';
const _HYDRATION_WAIT_TIMEOUT_MS = 3_500;
const _TOKEN_REFRESH_LEEWAY_MS = 5 * 60 * 1000;
const _AUTH_REJECTION_HEADER = 'x-dobi-auth-rejection';
const _AUTH_REQUEST_TIMEOUT_MS = 15_000;
// 本地热重载会重新导入完整 FastAPI 路由树，实测 worker 冷启动可达 23 秒。
// 显式登录应覆盖这个窗口；普通 API/refresh 仍保留更短的独立上限。
const _LOGIN_REQUEST_TIMEOUT_MS = 30_000;
const _LOGOUT_REQUEST_TIMEOUT_MS = 2_000;
const _AUTH_STATUS_POLL_TIMEOUT_MS = 8_000;
const _AUTH_TIMEOUT_MESSAGE = 'AUTH_REQUEST_TIMEOUT';
const _AMBIGUOUS_LOGIN_RECOVERY_DELAY_MS = 250;
const _WEB_SESSION_MIN_SECONDS = 14 * 86400;

type AuthSyncEventType = 'logout' | 'session-changed';
interface AuthSyncEvent {
  id: string;
  type: AuthSyncEventType;
  at: number;
  version: number;
}

let _authSyncChannel: BroadcastChannel | null = null;
const _processedAuthSyncEventIds = new Set<string>();
let _latestAuthSyncVersion = 0;
let _latestAuthSyncEventId = '';

export interface AuthUser {
  id: string;
  email: string;
  created_at?: string;
  isAdmin?: boolean;
  adminRole?: 'owner' | 'staff' | null;
  adminAllowedTabs?: string[];
  user_metadata?: {
    display_name?: string;
    bio?: string;
    avatar_url?: string;
    [key: string]: unknown;
  };
}

interface AuthResponseData {
  registration_event_id?: string | null;
  access_token: string;
  refresh_token?: string;
  session_expires_in?: number;
  user: {
    user_id: string;
    email: string;
    is_admin: boolean;
    admin_role?: 'owner' | 'staff' | null;
    admin_allowed_tabs?: string[];
  };
  expires_in: number;
  referral?: ReferralBinding | null;
  detail?: string;
}

interface ApiErrorPayload {
  code?: string;
  message?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: string | ApiErrorPayload;
  detail?: string | ApiErrorPayload;
}

interface ProviderEmailRequiredData {
  status: 'email-required';
  bind_ticket: string;
}

type AppleLoginResponseData = AuthResponseData | ProviderEmailRequiredData;
type AppleLoginResult = { status: 'authenticated' } | { status: 'email-required'; bindTicket: string };

export interface WechatQrSessionStartData {
  session_id: string;
  session_token: string;
  qr_url: string;
  expires_in: number;
}

export interface WechatQrSessionStatusData {
  status: 'pending' | 'authorized' | 'email-required' | 'completed' | 'failed' | 'expired';
  bind_ticket?: string;
  wechat_profile?: {
    nickname?: string;
    avatar_url?: string;
  };
  error?: string;
  auth?: AuthResponseData;
}

export interface AuthSecurityProvider {
  provider: 'email' | 'wechat' | string;
  verified: boolean;
  linked_at?: string | null;
  last_login_at?: string | null;
  metadata?: {
    nickname?: string;
    avatar_url?: string;
  };
}

export interface AuthSecurityData {
  email: string;
  email_verified: boolean;
  password_set: boolean;
  providers: AuthSecurityProvider[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;       // ★ 仅存内存，不持久化（默认 7 天过期）
  tokenExpiresAt: number;           // ★ access_token 过期时间戳 (ms)，仅内存
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  loginWithCode: (email: string, code: string, referral?: ReferralAuthPayload) => Promise<boolean>;
  loginWithGoogle: (credential: string, referral?: ReferralAuthPayload) => Promise<boolean>;
  loginWithApple: (identityToken: string, options?: {
    authorizationCode?: string;
    email?: string;
    fullName?: Record<string, unknown>;
    referral?: ReferralAuthPayload;
  }) => Promise<AppleLoginResult>;
  completeAppleEmailBinding: (
    bindTicket: string,
    email: string,
    code: string,
    referral?: ReferralAuthPayload,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: (forceRefresh?: boolean) => Promise<void>;
  updatePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  fetchAuthSecurity: () => Promise<AuthSecurityData>;
  sendVerificationCode: (email: string, purpose: 'reset_password' | 'login') => Promise<void>;
  resetPasswordWithCode: (email: string, code: string, newPassword: string) => Promise<void>;
  startWechatQrLogin: (signal?: AbortSignal) => Promise<WechatQrSessionStartData>;
  pollWechatQrLogin: (sessionId: string, sessionToken: string, signal?: AbortSignal) => Promise<WechatQrSessionStatusData>;
  consumeWechatQrLogin: (sessionId: string, sessionToken: string) => Promise<WechatQrSessionStatusData>;
  cancelWechatQrLogin: (sessionId: string, sessionToken: string, signal?: AbortSignal) => Promise<void>;
  completeWechatEmailBinding: (bindTicket: string, email: string, code: string, sessionToken?: string, referral?: ReferralAuthPayload) => Promise<WechatQrSessionStatusData>;
  clearError: () => void;
  cancelLoginAttempt: () => void;
}

type PersistedAuthState = Pick<AuthState, 'user' | 'isAuthenticated'>;

function _markAuthSessionChanged(): number {
  _authSessionVersion += 1;
  return _authSessionVersion;
}

function _markExplicitAuthAttempt(): number {
  _lastRefreshFailure = 0;
  return _markAuthSessionChanged();
}

function _beginLoginAttempt(): { version: number; signal: AbortSignal } {
  _activeLoginAbortController?.abort();
  _activeLoginAbortController = new AbortController();
  return {
    version: _markExplicitAuthAttempt(),
    signal: _activeLoginAbortController.signal,
  };
}

function _finishLoginAttempt(version: number): void {
  if (_isAuthSessionCurrent(version)) {
    _activeLoginAbortController = null;
  }
}

function _cancelLoginAttempt(): void {
  // Closing an already completed dialog is not an account/session change.
  // Invalidating that epoch would reject the new workspace's in-flight reads.
  if (!_activeLoginAbortController) return;
  _activeLoginAbortController?.abort();
  _activeLoginAbortController = null;
  _markAuthSessionChanged();
}

function _isAuthSessionCurrent(version: number): boolean {
  return version === _authSessionVersion;
}

/** 请求只读会话代次；登录/退出会推进，普通 token 续期不推进。 */
export function getAuthSessionVersion(): number {
  return _authSessionVersion;
}

function _isConfirmedSessionRejection(response: Response): boolean {
  return (
    (response.status === 401 || response.status === 403)
    && Boolean(response.headers.get(_AUTH_REJECTION_HEADER))
  );
}

/**
 * HttpOnly refresh cookie 由同源标签页共享，而模块级 Promise 只能合并单标签页请求。
 * Web Lock 把真正的 token 轮换跨标签页串行化，避免多个页面同时消费同一旧 token。
 */
interface CrossTabAuthMutationOptions {
  signal?: AbortSignal;
  interactive?: boolean;
}

async function _withCrossTabAuthMutationLock<T>(
  mutation: () => Promise<T>,
  options: CrossTabAuthMutationOptions = {},
): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return mutation();
  }

  const lockOptions: LockOptions = {
    mode: 'exclusive',
    // Web Locks 规范禁止 signal 与 steal 同时出现。显式登录会立即抢占，
    // 取消信号仍直接传给内部 fetch，因此不会丢失“关闭/重试即取消”的能力。
    ...(!options.interactive && options.signal ? { signal: options.signal } : {}),
    // 显式登录是当前用户的最高优先级操作。后台标签页可能因浏览器冻结而长期持锁；
    // Web Locks 的 steal 会立即释放旧锁，避免登录请求还没发出就一直排队。
    ...(options.interactive ? { steal: true } : {}),
  };
  return navigator.locks.request(_AUTH_MUTATION_LOCK_NAME, lockOptions, mutation);
}

function _resolveFreshAccessToken(
  accessToken: string | null,
  tokenExpiresAt: number,
): { token: string; expiresAt: number } | null {
  if (!accessToken) {
    return null;
  }

  if (tokenExpiresAt - Date.now() > _TOKEN_REFRESH_LEEWAY_MS) {
    return { token: accessToken, expiresAt: tokenExpiresAt };
  }

  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const expiresAt = Number(payload.exp) * 1000;
    if (expiresAt - Date.now() > _TOKEN_REFRESH_LEEWAY_MS) {
      return { token: accessToken, expiresAt };
    }
  } catch { /* invalid token, refresh below */ }

  return null;
}

function authCopy(key: string) {
  return translateClient('auth', key);
}

function extractAuthErrorMessage(payload: { detail?: unknown; error?: unknown }): string {
  const candidates = [payload.detail, payload.error];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      return candidate;
    }
    if (
      candidate
      && typeof candidate === 'object'
      && 'message' in candidate
      && typeof (candidate as { message?: unknown }).message === 'string'
    ) {
      return (candidate as { message: string }).message;
    }
  }
  return '';
}

function isProviderEmailRequired(data: unknown): data is ProviderEmailRequiredData {
  return Boolean(
    data
    && typeof data === 'object'
    && (data as { status?: unknown }).status === 'email-required'
    && typeof (data as { bind_ticket?: unknown }).bind_ticket === 'string'
  );
}

// 错误信息映射：将后端错误转换为用户友好的提示
function mapAuthError(errorMessage: string, fallbackKey = 'authFailed'): string {
  if (errorMessage.includes(_AUTH_TIMEOUT_MESSAGE)) {
    return authCopy('authTimeout');
  }

  const errorMap: Record<string, string> = {
    'Invalid login credentials': 'loginUserNotFound',
    'Email not confirmed': 'emailNotConfirmedError',
    'User already registered': 'emailAlreadyRegistered',
    'Password should be at least 6 characters': 'passwordTooShort',
    'Unable to validate email address: invalid format': 'invalidEmailFormat',
    'Network request failed': 'authNetworkFailed',
    'Failed to fetch': 'authNetworkFailed',
    'NetworkError': 'authNetworkFailed',
    '邮箱或密码错误': 'loginUserNotFound',
    '该账号未设置密码': 'passwordNotSetHint',
    'Google login is not configured': 'googleLoginNotConfigured',
    'Missing Google credential': 'googleLoginFailed',
    'Invalid Google credential': 'googleLoginFailed',
    'Google login failed': 'googleLoginFailed',
    'Google account has no email': 'googleLoginFailed',
    'Google email is not verified': 'googleLoginFailed',
    'Apple login is not configured': 'appleLoginNotConfigured',
    '缺少 Apple 登录令牌': 'appleLoginFailed',
    'Apple 登录凭证已过期，请重新登录': 'appleLoginFailed',
    'Apple 登录凭证无效': 'appleLoginFailed',
    'Apple 登录校验失败，请稍后重试': 'appleLoginFailed',
    'Apple 登录凭证缺少用户身份': 'appleLoginFailed',
    'Apple 绑定已过期': 'appleBindExpired',
    'Apple 绑定状态无效': 'appleBindFailed',
    '微信登录本地配置指向线上回调': 'wechatRedirectMismatch',
    '微信绑定已过期': 'wechatBindExpired',
    '该微信已绑定其他账号': 'wechatAlreadyBound',
    '该邮箱账号已绑定其他微信': 'wechatEmailAlreadyBound',
    '验证码无效或已过期': 'invalidVerificationCode',
    '验证码错误次数过多': 'verificationTooManyAttempts',
  };

  for (const [key, valueKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return authCopy(valueKey);
    }
  }

  return authCopy(fallbackKey);
}

function _isAmbiguousAuthTransportError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.message.includes(_AUTH_TIMEOUT_MESSAGE)
    || error.name === 'NetworkError'
    || error instanceof TypeError
  );
}

async function _fetchJsonWithTimeout<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = _AUTH_REQUEST_TIMEOUT_MS,
): Promise<{ res: Response; data: T }> {
  const controller = new AbortController();
  const externalSignal = init.signal;
  const abortFromExternalSignal = () => controller.abort();
  let timedOut = false;
  if (externalSignal?.aborted) {
    controller.abort();
  } else {
    externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
  }
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const res = await fetch(input, {
      ...init,
      signal: controller.signal,
    });
    const rawText = await res.text();
    let data = {} as T;
    if (rawText) {
      try {
        data = JSON.parse(rawText) as T;
      } catch {
        data = { detail: rawText } as T;
      }
    }
    return { res, data };
  } catch (err) {
    if (timedOut) {
      throw new Error(_AUTH_TIMEOUT_MESSAGE);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', abortFromExternalSignal);
  }
}

/**
 * ★ 迁移帮手：从旧版 localStorage 中读取 refreshToken（仅首次升级时使用）
 * 旧版 'octo-auth' 存储了 { state: { user, accessToken, refreshToken } }
 */
function _getLegacyRefreshToken(): string | null {
  try {
    const stored = localStorage.getItem('octo-auth');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.state?.refreshToken || null;
  } catch {
    return null;
  }
}

/** ★ 迁移清理：移除旧版 localStorage 中的 token 字段 */
function _cleanupLegacyTokens(): void {
  try {
    const stored = localStorage.getItem('octo-auth');
    if (!stored) return;
    const parsed = JSON.parse(stored);
    if (parsed?.state?.refreshToken || parsed?.state?.accessToken) {
      delete parsed.state.refreshToken;
      delete parsed.state.accessToken;
      localStorage.setItem('octo-auth', JSON.stringify(parsed));
    }
  } catch { /* ignore */ }
}

/**
 * ★ 在前端域设置/清除 logged_in cookie
 * 后端 refresh cookie 设置在对应 API 域的父域上，Next.js middleware 运行在当前前端域名，
 * 跨子域 cookie 不可见，导致 middleware 误判未登录 → 无限重定向。
 * 前端直接在自身域名设置此信号 cookie 来解决。
 */
function _getCookieDomain(): string {
  return resolveCookieDomainAttribute();
}

function _setLoggedInCookie(maxAge = 30 * 86400): void {
  const normalizedMaxAge = Math.max(_WEB_SESSION_MIN_SECONDS, maxAge);
  const secure = window.location.protocol === 'https:';
  document.cookie = `logged_in=1; path=/; max-age=${normalizedMaxAge}; samesite=lax${secure ? '; secure' : ''}${_getCookieDomain()}`;
}

function _clearLoggedInCookie(): void {
  const secure = window.location.protocol === 'https:';
  // 同时清除两个域上的 cookie（兼容旧版无 domain 和新版有 domain 的）
  document.cookie = `logged_in=; path=/; max-age=0; samesite=lax${secure ? '; secure' : ''}`;
  document.cookie = `logged_in=; path=/; max-age=0; samesite=lax${secure ? '; secure' : ''}${_getCookieDomain()}`;
}

function _hasLoggedInCookie(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.cookie
    .split(';')
    .map((part) => part.trim())
    .some((part) => part === 'logged_in=1');
}

function _recordAuthRecoveryEvent(reason: string, details: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const key = 'auth-recovery-events';
    const existing = JSON.parse(window.sessionStorage.getItem(key) || '[]');
    const events = Array.isArray(existing) ? existing : [];
    events.push({
      reason,
      details,
      at: new Date().toISOString(),
      path: window.location.pathname,
    });
    window.sessionStorage.setItem(key, JSON.stringify(events.slice(-20)));
  } catch { /* diagnostics only */ }
}

function _clearReferralAttributionIfBound(referral: ReferralBinding | null | undefined): void {
  if (referral?.bound) {
    clearStoredReferralAttribution();
  }
}

function _resetAuthScopedProjectState(): void {
  requestAuthScopedStateReset();
}

function _applyLocalLogoutState(options: { preserveSharedSession?: boolean } = {}): void {
  _activeLoginAbortController?.abort();
  _activeLoginAbortController = null;
  _markAuthSessionChanged();
  _anonymousSessionProbeCompleted = true;
  _lastRefreshFailure = 0;
  dispatchImageTemplateCacheInvalidated();
  if (!options.preserveSharedSession) {
    _clearLoggedInCookie();
  }
  useAuthStore.setState({
    user: null,
    accessToken: null,
    tokenExpiresAt: 0,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });
  if (!options.preserveSharedSession) {
    window.localStorage.removeItem('octo-auth');
    window.localStorage.removeItem('lepus-auth');
  }
  _resetAuthScopedProjectState();
}

function _parseAuthSyncEvent(raw: unknown): AuthSyncEvent | null {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (
      !value
      || typeof value !== 'object'
      || typeof (value as AuthSyncEvent).id !== 'string'
      || !['logout', 'session-changed'].includes((value as AuthSyncEvent).type)
      || !Number.isFinite((value as AuthSyncEvent).version)
    ) {
      return null;
    }
    return value as AuthSyncEvent;
  } catch {
    return null;
  }
}

function _handleAuthSyncEvent(raw: unknown): void {
  const event = _parseAuthSyncEvent(raw);
  if (!event || _processedAuthSyncEventIds.has(event.id)) return;

  try {
    const currentEvent = _parseAuthSyncEvent(
      window.localStorage.getItem(_AUTH_SYNC_STORAGE_KEY),
    );
    if (
      currentEvent
      && (
        currentEvent.version > event.version
        || (currentEvent.version === event.version && currentEvent.id !== event.id)
      )
    ) {
      _latestAuthSyncVersion = currentEvent.version;
      _latestAuthSyncEventId = currentEvent.id;
      return;
    }
  } catch {
    // Keep channel delivery working when storage is unavailable.
  }

  if (
    event.version < _latestAuthSyncVersion
    || (event.version === _latestAuthSyncVersion && event.id === _latestAuthSyncEventId)
  ) return;

  _latestAuthSyncVersion = event.version;
  _latestAuthSyncEventId = event.id;
  _processedAuthSyncEventIds.add(event.id);
  if (_processedAuthSyncEventIds.size > 20) {
    const oldest = _processedAuthSyncEventIds.values().next().value;
    if (oldest) _processedAuthSyncEventIds.delete(oldest);
  }

  _applyLocalLogoutState({ preserveSharedSession: event.type === 'session-changed' });
  if (window.location.pathname === '/kagemane') {
    if (event.type === 'session-changed') {
      const version = _authSessionVersion;
      // Drain probes belonging to the superseded identity before recovering
      // the new cookie session; they must not swallow this forced probe.
      void Promise.allSettled([_sessionCheckPromise, _forcedSessionCheckPromise])
        .then(async () => {
          if (_isAuthSessionCurrent(version)) await useAuthStore.getState().checkSession(true);
        }).catch(() => { /* Public browsing remains available offline. */ });
    }
    return;
  }
  if (event.type === 'logout') {
    window.location.replace(POST_LOGOUT_PATH);
    return;
  }
  window.location.reload();
}

function _broadcastAuthSyncEvent(type: AuthSyncEventType): void {
  if (typeof window === 'undefined') return;
  let persistedVersion = 0;
  try {
    persistedVersion = _parseAuthSyncEvent(
      window.localStorage.getItem(_AUTH_SYNC_STORAGE_KEY),
    )?.version ?? 0;
  } catch {
    // Fall back to this tab's monotonic epoch.
  }
  const version = Math.max(Date.now(), persistedVersion + 1, _latestAuthSyncVersion + 1);
  const event: AuthSyncEvent = {
    id: typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`,
    type,
    at: Date.now(),
    version,
  };
  _latestAuthSyncVersion = version;
  _latestAuthSyncEventId = event.id;
  _processedAuthSyncEventIds.add(event.id);
  try {
    window.localStorage.setItem(_AUTH_SYNC_STORAGE_KEY, JSON.stringify(event));
  } catch {
    // BroadcastChannel remains the primary path when storage is unavailable.
  }
  _authSyncChannel?.postMessage(event);
}

function _initializeCrossTabAuthSync(): void {
  if (typeof window === 'undefined') return;
  const globalWindow = window as Window & { __dobiAuthSyncCleanup?: () => void };
  globalWindow.__dobiAuthSyncCleanup?.();
  try {
    const currentEvent = _parseAuthSyncEvent(
      window.localStorage.getItem(_AUTH_SYNC_STORAGE_KEY),
    );
    _latestAuthSyncVersion = currentEvent?.version ?? 0;
    _latestAuthSyncEventId = currentEvent?.id ?? '';
  } catch {
    _latestAuthSyncVersion = 0;
    _latestAuthSyncEventId = '';
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === _AUTH_SYNC_STORAGE_KEY && event.newValue) {
      _handleAuthSyncEvent(event.newValue);
    }
  };
  window.addEventListener('storage', handleStorage);

  if (typeof BroadcastChannel !== 'undefined') {
    _authSyncChannel = new BroadcastChannel(_AUTH_SYNC_CHANNEL_NAME);
    _authSyncChannel.addEventListener('message', event => _handleAuthSyncEvent(event.data));
  }

  globalWindow.__dobiAuthSyncCleanup = () => {
    window.removeEventListener('storage', handleStorage);
    _authSyncChannel?.close();
    _authSyncChannel = null;
  };
}

const authPersistStorage: PersistStorage<PersistedAuthState> = {
  getItem: (name: string): StorageValue<PersistedAuthState> | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    const raw = window.localStorage.getItem(name);
    if (raw == null) {
      return null;
    }

    if (raw.trim().length === 0) {
      window.localStorage.removeItem(name);
      return null;
    }

    try {
      return JSON.parse(raw) as StorageValue<PersistedAuthState>;
    } catch (error) {
      debugError('[Auth] invalid persisted auth state, clearing storage:', error);
      window.localStorage.removeItem(name);
      return null;
    }
  },
  setItem: (name: string, value: StorageValue<PersistedAuthState>): void => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string): void => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const applyAuthSuccess = async (
        data: AuthResponseData,
        expectedSessionVersion?: number,
        notifyPeers = false,
      ): Promise<boolean> => {
        if (
          expectedSessionVersion !== undefined
          && !_isAuthSessionCurrent(expectedSessionVersion)
        ) {
          return false;
        }
        const previousUserId = get().user?.id ?? null;
        if (previousUserId && previousUserId !== data.user.user_id) {
          await _resetAuthScopedProjectState();
        }
        if (
          expectedSessionVersion !== undefined
          && !_isAuthSessionCurrent(expectedSessionVersion)
        ) {
          return false;
        }
        set({
          user: {
            id: data.user.user_id,
            email: data.user.email,
            isAdmin: data.user.is_admin,
            adminRole: data.user.admin_role ?? (data.user.is_admin ? 'owner' : null),
            adminAllowedTabs: data.user.admin_allowed_tabs ?? [],
          },
          accessToken: data.access_token,
          tokenExpiresAt: Date.now() + data.expires_in * 1000,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        _clearReferralAttributionIfBound(data.referral);
        _setLoggedInCookie(data.session_expires_in);
        // Only the profile-creation winner receives a registration receipt.
        // Existing login, session recovery and provider linking never count as signup.
        if (data.registration_event_id && !data.user.is_admin) {
          stageXRegistrationReceipt(data.user.user_id, data.registration_event_id);
          void import('@/lib/marketing/x-conversions').then(({ trackXRegistration }) =>
            trackXRegistration(data.user.user_id, data.registration_event_id!),
          ).catch(() => { /* Optional telemetry cannot fail authentication. */ });
        }
        if (notifyPeers) {
          _broadcastAuthSyncEvent('session-changed');
        }
        return true;
      };

      const recoverAmbiguousLogin = async (
        error: unknown,
        expectedSessionVersion: number,
      ): Promise<boolean> => {
        if (!_isAmbiguousAuthTransportError(error)) {
          return false;
        }

        _recordAuthRecoveryEvent('ambiguous_login_response');
        await new Promise(resolve => setTimeout(resolve, _AMBIGUOUS_LOGIN_RECOVERY_DELAY_MS));
        if (!_isAuthSessionCurrent(expectedSessionVersion)) {
          return false;
        }

        try {
          const { res, data } = await _fetchJsonWithTimeout<ApiEnvelope<AuthResponseData>>(
            `${API_BASE}/auth/session`,
            {
              method: 'POST',
              headers: _clientHeaders(),
              credentials: 'include',
            },
            _LOGIN_RECOVERY_TIMEOUT_MS,
          );
          if (
            res.ok
            && data.success
            && await applyAuthSuccess(data.data, expectedSessionVersion, true)
          ) {
            _recordAuthRecoveryEvent('ambiguous_login_recovered');
            return true;
          }
        } catch {
          // One bounded probe is enough. The user can retry without being trapped.
        }

        _recordAuthRecoveryEvent('ambiguous_login_not_recovered');
        return false;
      };

      return {
      user: null,
      accessToken: null,
      tokenExpiresAt: 0,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      login: async (email: string, password: string) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });
        
        try {
          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<AuthResponseData & { detail?: string }>(
              `${API_BASE}/auth/login`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: JSON.stringify({ email, password }),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok) {
            throw new Error(data.detail || '');
          }

          return applyAuthSuccess(data, attemptVersion, true);
        } catch (err) {
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return true;
          }
          if (!_isAuthSessionCurrent(attemptVersion)) {
            return false;
          }
          const rawMessage = err instanceof Error ? err.message : '';
          const message = mapAuthError(rawMessage, 'loginFailed');
          _markAuthSessionChanged();
          set({ 
            isLoading: false, 
            error: message,
            user: null,
            accessToken: null,
            tokenExpiresAt: 0,
            isAuthenticated: false,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      loginWithCode: async (email: string, code: string, referral?: ReferralAuthPayload) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });
        
        try {
          const body: Record<string, string> = { email, code };
          if (referral?.referral_code) {
            body.referral_code = referral.referral_code;
          }
          if (referral?.referral_click_id) {
            body.referral_click_id = referral.referral_click_id;
          }
          if (referral?.campaign_key) {
            body.campaign_key = referral.campaign_key;
          }

          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<AuthResponseData & { detail?: string }>(
              `${API_BASE}/auth/login-with-code`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: JSON.stringify(body),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok) {
            throw new Error(data.detail || '');
          }

          return applyAuthSuccess(data, attemptVersion, true);
        } catch (err) {
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return true;
          }
          if (!_isAuthSessionCurrent(attemptVersion)) {
            return false;
          }
          const rawMessage = err instanceof Error ? err.message : '';
          const message = mapAuthError(rawMessage, 'loginFailed');
          _markAuthSessionChanged();
          set({ 
            isLoading: false, 
            error: message,
            user: null,
            accessToken: null,
            tokenExpiresAt: 0,
            isAuthenticated: false,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      loginWithGoogle: async (credential: string, referral?: ReferralAuthPayload) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });

        try {
          const body: Record<string, string> = { credential };
          if (referral?.referral_code) body.referral_code = referral.referral_code;
          if (referral?.referral_click_id) body.referral_click_id = referral.referral_click_id;
          if (referral?.campaign_key) body.campaign_key = referral.campaign_key;

          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<ApiEnvelope<AuthResponseData>>(
              `${API_BASE}/auth/google/login`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: JSON.stringify(body),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok || !data.success) {
            throw new Error(extractAuthErrorMessage(data));
          }
          if (!await applyAuthSuccess(data.data, attemptVersion, true)) {
            throw new DOMException('Login attempt superseded', 'AbortError');
          }
          return true;
        } catch (err) {
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return true;
          }
          if (!_isAuthSessionCurrent(attemptVersion)) {
            throw err;
          }
          const message = mapAuthError(
            err instanceof Error ? err.message : '',
            'googleLoginFailed',
          );
          _markAuthSessionChanged();
          set({
            isLoading: false,
            error: message,
            user: null,
            accessToken: null,
            tokenExpiresAt: 0,
            isAuthenticated: false,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      loginWithApple: async (identityToken: string, options = {}) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });

        try {
          const body: Record<string, unknown> = { identity_token: identityToken };
          if (options.authorizationCode) body.authorization_code = options.authorizationCode;
          if (options.email) body.email = options.email;
          if (options.fullName) body.full_name = options.fullName;
          if (options.referral?.referral_code) {
            body.referral_code = options.referral.referral_code;
          }
          if (options.referral?.referral_click_id) {
            body.referral_click_id = options.referral.referral_click_id;
          }
          if (options.referral?.campaign_key) {
            body.campaign_key = options.referral.campaign_key;
          }

          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<ApiEnvelope<AppleLoginResponseData>>(
              `${API_BASE}/auth/apple/login`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: JSON.stringify(body),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok || !data.success) {
            throw new Error(extractAuthErrorMessage(data));
          }
          if (isProviderEmailRequired(data.data)) {
            set({ isLoading: false, error: null });
            return { status: 'email-required', bindTicket: data.data.bind_ticket };
          }
          if (!await applyAuthSuccess(data.data, attemptVersion, true)) {
            throw new DOMException('Login attempt superseded', 'AbortError');
          }
          return { status: 'authenticated' };
        } catch (err) {
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return { status: 'authenticated' };
          }
          if (!_isAuthSessionCurrent(attemptVersion)) {
            throw err;
          }
          const message = mapAuthError(
            err instanceof Error ? err.message : '',
            'appleLoginFailed',
          );
          _markAuthSessionChanged();
          set({
            isLoading: false,
            error: message,
            user: null,
            accessToken: null,
            tokenExpiresAt: 0,
            isAuthenticated: false,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      completeAppleEmailBinding: async (
        bindTicket: string,
        email: string,
        code: string,
        referral?: ReferralAuthPayload,
      ) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });

        try {
          const body: Record<string, string> = {
            bind_ticket: bindTicket,
            email,
            code,
          };
          if (referral?.referral_code) body.referral_code = referral.referral_code;
          if (referral?.referral_click_id) body.referral_click_id = referral.referral_click_id;
          if (referral?.campaign_key) body.campaign_key = referral.campaign_key;

          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<ApiEnvelope<AuthResponseData>>(
              `${API_BASE}/auth/apple/complete-bind`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: JSON.stringify(body),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok || !data.success) {
            throw new Error(extractAuthErrorMessage(data));
          }
          if (!await applyAuthSuccess(data.data, attemptVersion, true)) {
            throw new DOMException('Login attempt superseded', 'AbortError');
          }
          return true;
        } catch (err) {
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return true;
          }
          if (!_isAuthSessionCurrent(attemptVersion)) {
            throw err;
          }
          const message = mapAuthError(
            err instanceof Error ? err.message : '',
            'appleBindFailed',
          );
          _markAuthSessionChanged();
          set({
            isLoading: false,
            error: message,
            user: null,
            accessToken: null,
            tokenExpiresAt: 0,
            isAuthenticated: false,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      logout: async () => {
        const accessToken = get().accessToken;
        _applyLocalLogoutState();
        _broadcastAuthSyncEvent('logout');

        // 退出抢占后台 refresh 锁；本地缓存已同步清理，只等待服务端撤销会话。
        const serverLogout = _withCrossTabAuthMutationLock(() => (
          _fetchJsonWithTimeout<{ success?: boolean }>(`${API_BASE}/auth/logout`, {
            method: 'POST',
            headers: _clientHeaders(
              accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
            ),
            credentials: 'include',
          }, _LOGOUT_REQUEST_TIMEOUT_MS)
        ), { interactive: true });
        try {
          const { res, data } = await serverLogout;
          if (!res.ok || data.success === false) {
            debugError('[Auth] server logout did not confirm session revocation');
          }
        } catch {
          debugError('[Auth] server logout did not confirm session revocation');
        }
        _clearLoggedInCookie();
      },

      checkSession: async (forceRefresh = false) => {
        if (forceRefresh) {
          if (_forcedSessionCheckPromise) {
            await _forcedSessionCheckPromise;
            return;
          }

          const sessionVersion = _authSessionVersion;
          const { accessToken, isAuthenticated } = get();
          const hasLoggedInCookie = _hasLoggedInCookie();
          if (!isAuthenticated && !accessToken && !hasLoggedInCookie) {
            set({ isLoading: false });
            return;
          }

          const legacyRefreshToken = _getLegacyRefreshToken();
          const recoveryController = new AbortController();
          // Cover Web Lock queue time as well as transport time for legacy rotation.
          const recoveryTimeout = setTimeout(() => recoveryController.abort(), _REFRESH_REQUEST_TIMEOUT_MS);
          const recoverSession = async () => {
            const body = legacyRefreshToken
              ? JSON.stringify({ refresh_token: legacyRefreshToken })
              : '{}';
            const recoveryEndpoint = legacyRefreshToken ? 'refresh' : 'session';

            for (let attempt = 0; attempt < _REFRESH_COOKIE_SYNC_MAX_ATTEMPTS; attempt++) {
              try {
                const { res, data } = await _fetchJsonWithTimeout<
                  AuthResponseData | ApiEnvelope<AuthResponseData>
                >(
                  `${API_BASE}/auth/${recoveryEndpoint}`,
                  {
                    method: 'POST',
                    headers: _jsonHeaders(),
                    credentials: 'include',
                    body,
                    signal: recoveryController.signal,
                  },
                  _REFRESH_REQUEST_TIMEOUT_MS,
                );
                if (!_isAuthSessionCurrent(sessionVersion)) return;

                if (res.ok) {
                  const authData = legacyRefreshToken
                    ? data as AuthResponseData
                    : (data as ApiEnvelope<AuthResponseData>).data;
                  if (!authData) {
                    _lastRefreshFailure = Date.now();
                    _recordAuthRecoveryEvent('refresh_malformed_success');
                    set({ isLoading: false });
                    return;
                  }
                  await applyAuthSuccess(authData, sessionVersion);
                  _lastRefreshFailure = 0;
                  if (legacyRefreshToken) _cleanupLegacyTokens();
                  return;
                }
                if (res.status === 409 && attempt < _REFRESH_COOKIE_SYNC_MAX_ATTEMPTS - 1) {
                  _recordAuthRecoveryEvent('refresh_cookie_sync_retry', { attempt: attempt + 1 });
                  await new Promise(resolve => setTimeout(resolve, _REFRESH_COOKIE_SYNC_RETRY_DELAY_MS));
                  continue;
                }
                if (!_isConfirmedSessionRejection(res)) {
                  _lastRefreshFailure = Date.now();
                  _recordAuthRecoveryEvent('refresh_temporary_failure', { status: res.status });
                  set({ isLoading: false });
                  return;
                }

                _recordAuthRecoveryEvent('refresh_auth_rejected', { status: res.status });
                _clearLoggedInCookie();
                _lastRefreshFailure = Date.now();
                set({
                  user: null,
                  accessToken: null,
                  tokenExpiresAt: 0,
                  isAuthenticated: false,
                  isLoading: false,
                });
                await _resetAuthScopedProjectState();
                return;
              } catch {
                if (!_isAuthSessionCurrent(sessionVersion)) return;
                _lastRefreshFailure = Date.now();
                _recordAuthRecoveryEvent('refresh_network_failure');
                set({ isLoading: false });
                return;
              }
            }
          };
          // /session does not rotate the shared cookie and must not queue behind
          // a frozen tab's auth mutation lock. Only legacy /refresh needs the lock.
          _forcedSessionCheckPromise = legacyRefreshToken
            ? _withCrossTabAuthMutationLock(recoverSession, { signal: recoveryController.signal })
            : recoverSession();

          try {
            await _forcedSessionCheckPromise;
          } catch {
            if (_isAuthSessionCurrent(sessionVersion)) {
              _lastRefreshFailure = Date.now();
              _recordAuthRecoveryEvent('refresh_lock_timeout');
              set({ isLoading: false });
            }
          } finally {
            clearTimeout(recoveryTimeout);
            _forcedSessionCheckPromise = null;
          }
          return;
        }

        if (_sessionCheckPromise) {
          await _sessionCheckPromise;
          return;
        }

        _sessionCheckPromise = (async () => {
          const sessionVersion = _authSessionVersion;
          const { accessToken, tokenExpiresAt, isAuthenticated } = get();
          const hasLoggedInCookie = _hasLoggedInCookie();

          // 发版/缓存修复可能清掉前端状态与 logged_in，却保留 API 域的
          // HttpOnly refresh cookie。每次页面生命周期允许一次无信号恢复探测。
          const shouldProbeHttpOnlySession =
            !isAuthenticated
            && !accessToken
            && !hasLoggedInCookie
            && !_anonymousSessionProbeCompleted;
          if (!isAuthenticated && !accessToken && !hasLoggedInCookie && !shouldProbeHttpOnlySession) {
            _recordAuthRecoveryEvent('skip_repeated_anonymous_session_probe');
            set({ isLoading: false });
            return;
          }
          if (shouldProbeHttpOnlySession) {
            _anonymousSessionProbeCompleted = true;
            _recordAuthRecoveryEvent('probe_http_only_session_without_local_signal');
          }

          const freshToken = _resolveFreshAccessToken(accessToken, tokenExpiresAt);
          if (freshToken) {
            set({
              tokenExpiresAt: freshToken.expiresAt,
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          }

          if (_lastRefreshFailure && Date.now() - _lastRefreshFailure < _REFRESH_COOLDOWN_MS) {
            set({ isLoading: false });
            return;
          }

          const legacyRefreshToken = _getLegacyRefreshToken();
          try {
            const { res, data } = await _fetchJsonWithTimeout<ApiEnvelope<AuthResponseData>>(
              `${API_BASE}/auth/session`,
              {
                method: 'POST',
                headers: _jsonHeaders(),
                credentials: 'include',
                body: legacyRefreshToken
                  ? JSON.stringify({ refresh_token: legacyRefreshToken })
                  : '{}',
              },
              _SESSION_RECOVERY_TIMEOUT_MS,
            );
            if (!_isAuthSessionCurrent(sessionVersion)) return;

            if (res.ok && data.success) {
              await applyAuthSuccess(data.data, sessionVersion);
              _lastRefreshFailure = 0;
              if (legacyRefreshToken) _cleanupLegacyTokens();
              return;
            }
            if (!_isConfirmedSessionRejection(res)) {
              _lastRefreshFailure = Date.now();
              _recordAuthRecoveryEvent('session_temporary_failure', { status: res.status });
              set({ isLoading: false });
              return;
            }

            _recordAuthRecoveryEvent('session_auth_rejected', { status: res.status });
            _clearLoggedInCookie();
            _lastRefreshFailure = Date.now();
            set({
              user: null,
              accessToken: null,
              tokenExpiresAt: 0,
              isAuthenticated: false,
              isLoading: false,
            });
            await _resetAuthScopedProjectState();
          } catch {
            if (!_isAuthSessionCurrent(sessionVersion)) return;
            _lastRefreshFailure = Date.now();
            _recordAuthRecoveryEvent('session_network_failure');
            set({ isLoading: false });
          }
        })();

        try {
          await _sessionCheckPromise;
        } finally {
          _sessionCheckPromise = null;
        }
      },

      updatePassword: async (newPassword: string, currentPassword?: string) => {
        set({ isLoading: true, error: null });
        
        try {
          let accessToken = await ensureValidToken();
          if (!accessToken) {
            throw new Error('登录已过期，请重新登录');
          }
          const body: Record<string, string> = { new_password: newPassword };
          if (currentPassword) {
            body.current_password = currentPassword;
          }

          let res = await fetch(`${API_BASE}/auth/update-password`, {
            method: 'POST',
            headers: _jsonHeaders({
              'Authorization': `Bearer ${accessToken}`,
            }),
            credentials: 'include',
            body: JSON.stringify(body),
          });
          if (res.status === 401) {
            accessToken = await ensureValidToken(true);
            if (!accessToken) {
              throw new Error('登录已过期，请重新登录');
            }
            res = await fetch(`${API_BASE}/auth/update-password`, {
              method: 'POST',
              headers: _jsonHeaders({
                'Authorization': `Bearer ${accessToken}`,
              }),
              credentials: 'include',
              body: JSON.stringify(body),
            });
          }

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || '');
          }

          set({ isLoading: false, error: null });
        } catch (err) {
          const rawMessage = err instanceof Error ? err.message : '';
          const message = mapAuthError(rawMessage, 'passwordUpdateFailed');
          set({ 
            isLoading: false, 
            error: message,
          });
          throw new Error(message);
        }
      },

      fetchAuthSecurity: async () => {
        let accessToken = await ensureValidToken();
        if (!accessToken) {
          throw new Error(mapAuthError('登录已过期，请重新登录', 'securityLoadFailed'));
        }
        let res = await fetch(`${API_BASE}/auth/security`, {
          method: 'GET',
          headers: _jsonHeaders({
            'Authorization': `Bearer ${accessToken}`,
          }),
          credentials: 'include',
        });
        if (res.status === 401) {
          accessToken = await ensureValidToken(true);
          if (!accessToken) {
            throw new Error(mapAuthError('登录已过期，请重新登录', 'securityLoadFailed'));
          }
          res = await fetch(`${API_BASE}/auth/security`, {
            method: 'GET',
            headers: _jsonHeaders({
              'Authorization': `Bearer ${accessToken}`,
            }),
            credentials: 'include',
          });
        }
        const data = await res.json() as ApiEnvelope<AuthSecurityData>;
        if (!res.ok || !data.success) {
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'securityLoadFailed'));
        }
        return data.data;
      },

      clearError: () => set({ error: null }),
      cancelLoginAttempt: () => {
        _cancelLoginAttempt();
        set({ isLoading: false, error: null });
      },

      sendVerificationCode: async (email: string, purpose: 'reset_password' | 'login') => {
        const { res, data } = await _fetchJsonWithTimeout<{ detail?: unknown; error?: unknown }>(`${API_BASE}/auth/send-code`, {
          method: 'POST',
          headers: _jsonHeaders(),
          body: JSON.stringify({ email, purpose }),
        });
        if (!res.ok) {
          if (res.status === 429) {
            throw new Error(authCopy('verificationRateLimited'));
          }
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'sendVerificationCodeFailed'));
        }
      },

      resetPasswordWithCode: async (email: string, code: string, newPassword: string) => {
        const { res, data } = await _fetchJsonWithTimeout<{ detail?: unknown; error?: unknown }>(`${API_BASE}/auth/reset-password`, {
          method: 'POST',
          headers: _jsonHeaders(),
          body: JSON.stringify({ email, code, new_password: newPassword }),
        });
        if (!res.ok) {
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'resetWithCodeFailed'));
        }
      },

      startWechatQrLogin: async (signal?: AbortSignal) => {
        const { res, data } = await _fetchJsonWithTimeout<ApiEnvelope<WechatQrSessionStartData>>(`${API_BASE}/auth/wechat/qr-session`, {
          method: 'POST',
          headers: _clientHeaders(),
          credentials: 'include',
          signal,
        });
        if (!res.ok || !data.success) {
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'wechatStartFailed'));
        }
        return data.data;
      },

      pollWechatQrLogin: async (sessionId: string, sessionToken: string, signal?: AbortSignal) => {
        const { res, data } = await _fetchJsonWithTimeout<ApiEnvelope<WechatQrSessionStatusData>>(
          `${API_BASE}/auth/wechat/qr-session/${encodeURIComponent(sessionId)}`,
          {
            headers: _clientHeaders({ [WECHAT_QR_SESSION_TOKEN_HEADER]: sessionToken }),
            credentials: 'include',
            signal,
          },
          _AUTH_STATUS_POLL_TIMEOUT_MS,
        );
        if (!res.ok || !data.success) {
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'wechatStatusFailed'));
        }
        return data.data;
      },

      consumeWechatQrLogin: async (sessionId: string, sessionToken: string) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });
        try {
          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<ApiEnvelope<WechatQrSessionStatusData>>(
              `${API_BASE}/auth/wechat/qr-session/${encodeURIComponent(sessionId)}/consume`,
              {
                method: 'POST',
                headers: _clientHeaders({ [WECHAT_QR_SESSION_TOKEN_HEADER]: sessionToken }),
                credentials: 'include',
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });
          if (!res.ok || !data.success || !data.data.auth) {
            throw new Error(extractAuthErrorMessage(data));
          }
          if (!await applyAuthSuccess(data.data.auth, attemptVersion, true)) {
            throw new DOMException('Login attempt superseded', 'AbortError');
          }
          return data.data;
        } catch (err) {
          if (!_isAuthSessionCurrent(attemptVersion)) {
            throw err;
          }
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return {
              status: 'completed',
              auth: {
                access_token: get().accessToken || '',
                refresh_token: '',
                user: {
                  user_id: get().user?.id || '',
                  email: get().user?.email || '',
                  is_admin: Boolean(get().user?.isAdmin),
                  admin_role: get().user?.adminRole,
                  admin_allowed_tabs: get().user?.adminAllowedTabs,
                },
                expires_in: Math.max(0, Math.floor((get().tokenExpiresAt - Date.now()) / 1000)),
              },
            };
          }
          throw err;
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },

      cancelWechatQrLogin: async (sessionId: string, sessionToken: string, signal?: AbortSignal) => {
        const { res, data } = await _fetchJsonWithTimeout<ApiEnvelope<{ status: string }>>(
          `${API_BASE}/auth/wechat/qr-session/${encodeURIComponent(sessionId)}/cancel`,
          {
            method: 'POST',
            headers: _clientHeaders({ [WECHAT_QR_SESSION_TOKEN_HEADER]: sessionToken }),
            credentials: 'include',
            signal,
          },
          _AUTH_STATUS_POLL_TIMEOUT_MS,
        );
        if (!res.ok || !data.success) {
          throw new Error(mapAuthError(extractAuthErrorMessage(data), 'wechatStatusFailed'));
        }
      },

      completeWechatEmailBinding: async (bindTicket: string, email: string, code: string, sessionToken?: string, referral?: ReferralAuthPayload) => {
        const { version: attemptVersion, signal } = _beginLoginAttempt();
        set({ isLoading: true, error: null });

        try {
          const body: Record<string, string> = {
            bind_ticket: bindTicket,
            email,
            code,
          };
          if (referral?.referral_code) {
            body.referral_code = referral.referral_code;
          }
          if (referral?.referral_click_id) {
            body.referral_click_id = referral.referral_click_id;
          }
          if (referral?.campaign_key) {
            body.campaign_key = referral.campaign_key;
          }

          const { res, data } = await _withCrossTabAuthMutationLock(() => (
            _fetchJsonWithTimeout<ApiEnvelope<{ status: 'completed'; auth: AuthResponseData }>>(
              `${API_BASE}/auth/wechat/complete-bind`,
              {
                method: 'POST',
                headers: _jsonHeaders(sessionToken ? { [WECHAT_QR_SESSION_TOKEN_HEADER]: sessionToken } : undefined),
                credentials: 'include',
                body: JSON.stringify(body),
                signal,
              },
              _LOGIN_REQUEST_TIMEOUT_MS,
            )
          ), { interactive: true, signal });

          if (!res.ok || !data.success) {
            throw new Error(extractAuthErrorMessage(data));
          }

          if (!await applyAuthSuccess(data.data.auth, attemptVersion, true)) {
            throw new DOMException('Login attempt superseded', 'AbortError');
          }
          return data.data;
        } catch (err) {
          if (!_isAuthSessionCurrent(attemptVersion)) {
            throw err;
          }
          if (await recoverAmbiguousLogin(err, attemptVersion)) {
            return {
              status: 'completed',
              auth: {
                access_token: get().accessToken || '',
                refresh_token: '',
                user: {
                  user_id: get().user?.id || '',
                  email: get().user?.email || email,
                  is_admin: Boolean(get().user?.isAdmin),
                  admin_role: get().user?.adminRole,
                  admin_allowed_tabs: get().user?.adminAllowedTabs,
                },
                expires_in: Math.max(0, Math.floor((get().tokenExpiresAt - Date.now()) / 1000)),
              },
            };
          }
          const rawMessage = err instanceof Error ? err.message : '';
          const message = mapAuthError(rawMessage, 'wechatBindFailed');
          _markAuthSessionChanged();
          set({
            isLoading: false,
            error: message,
          });
          throw new Error(message);
        } finally {
          _finishLoginAttempt(attemptVersion);
        }
      },
    };
    },
    {
      name: 'octo-auth',
      storage: authPersistStorage,
      // ★ 仅持久化 user 信息 — accessToken 不落盘，防止 XSS 窃取 JWT
      // access_token 通过 HttpOnly cookie refresh 恢复
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

_initializeCrossTabAuthSync();

type AuthPersistApi = {
  hasHydrated?: () => boolean;
  onFinishHydration?: (listener: () => void) => (() => void) | void;
};

function _getPersistApi(): AuthPersistApi | undefined {
  return (useAuthStore as typeof useAuthStore & { persist?: AuthPersistApi }).persist;
}

/**
 * 首屏首次加载时，persist hydration 与受保护接口请求存在竞态。
 * 如果在 hydration 前就判定“未登录”，会让 /explore 首登立刻打出 401。
 */
async function _waitForPersistHydration(timeoutMs = _HYDRATION_WAIT_TIMEOUT_MS): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const persistApi = _getPersistApi();
  const alreadyHydrated = persistApi?.hasHydrated?.() ?? true;
  if (alreadyHydrated) {
    return;
  }

  await new Promise<void>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | void;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      if (timer) {
        clearTimeout(timer);
      }
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      resolve();
    };

    try {
      unsubscribe = persistApi?.onFinishHydration?.(finish);
    } catch {
      resolve();
      return;
    }

    timer = setTimeout(finish, timeoutMs);
  });
}

// 获取当前 token 的辅助函数（供 API 调用使用）
export function getAuthToken(): string | null {
  return useAuthStore.getState().accessToken;
}

// 获取当前用户 ID 的辅助函数
export function getCurrentUserId(): string | null {
  return useAuthStore.getState().user?.id || null;
}

/**
 * ★ 确保 token 有效 - 在关键 API 调用前使用
 * - access_token 仅存内存，页面刷新后需通过 HttpOnly cookie 重新获取
 * - 如果 token 即将过期或已过期，会尝试刷新
 * - 内置去重锁：多个并发调用共享同一次刷新
 */
export async function ensureValidToken(forceRefresh = false): Promise<string | null> {
  await _waitForPersistHydration();
  const state = useAuthStore.getState();
  const currentToken = state.accessToken;

  if (forceRefresh) {
    if (!state.isAuthenticated && !currentToken && !_hasLoggedInCookie()) {
      return null;
    }
    return _refreshTokenWithLock(true);
  }
  
  if (!currentToken) {
    // token 不在内存中（页面刷新 / 部署后），但用户曾登录 → cookie 刷新
    if (state.isAuthenticated || _hasLoggedInCookie()) {
      // 冷却期内跳过，避免后端宕机时请求风暴
      if (_lastRefreshFailure && Date.now() - _lastRefreshFailure < _REFRESH_COOLDOWN_MS) {
        return null;
      }
      return _refreshTokenWithLock();
    }
    return null;
  }
  
  // 检查 token 是否即将过期
  const timeUntilExpiry = state.tokenExpiresAt - Date.now();
  if (timeUntilExpiry < 5 * 60 * 1000) {
    return _refreshTokenWithLock();
  }
  
  return currentToken;
}

/**
 * 带去重锁的 token 刷新 — 多个并发调用共享同一个 Promise
 */
async function _refreshTokenWithLock(forceRefresh = false): Promise<string | null> {
  if (_refreshPromise) {
    return _refreshPromise;
  }

  _refreshPromise = (async () => {
    try {
      const state = useAuthStore.getState();
      await state.checkSession(forceRefresh);
      return useAuthStore.getState().accessToken;
    } catch {
      return null;
    }
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}
